/**
 * Eira Theme — Predictive Search Modal
 * Spotlight-style centered modal using Shopify's Predictive Search API.
 *
 * Architecture:
 *  - <predictive-search-modal> — the modal shell (fixed overlay)
 *  - Triggered by any element with [data-search-trigger]
 *  - Fetches predictive-search section HTML on every keypress (debounced)
 *  - Renders query suggestions, products, collections, articles, pages
 *  - Keyboard navigation: ↑↓ to move, Enter to select, Esc to close
 *  - ⌘K / Ctrl+K to open from anywhere
 */

(() => {
  'use strict';

  /* ── Utility: debounce ─────────────────────────────────────── */
  function debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  /* ── Utility: trap focus inside element ───────────────────── */
  function trapFocus(el) {
    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function handleKeydown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    el.addEventListener('keydown', handleKeydown);
    return () => el.removeEventListener('keydown', handleKeydown);
  }

  /* ════════════════════════════════════════════════════════════
     PredictiveSearchModal — custom element
     ════════════════════════════════════════════════════════════ */
  class PredictiveSearchModal extends HTMLElement {
    constructor() {
      super();
      this.backdrop = null;
      this.modal = null;
      this.input = null;
      this.closeBtn = null;
      this.voiceBtn = null;
      this.spinner = null;
      this.body = null;
      this.status = null;

      this._abortController = null;
      this._lastQuery = '';
      this._trapCleanup = null;
      this._isOpen = false;
      this._popularTerms = [];
      this._recognition = null;
    }

    connectedCallback() {
      this.backdrop = this.querySelector('.bc-search-backdrop');
      this.modal = this.querySelector('.bc-search-modal');
      this.input = this.querySelector('.bc-search-modal__input');
      this.closeBtn = this.querySelector('.bc-search-modal__close');
      this.voiceBtn = this.querySelector('.bc-search-modal__voice');
      this.spinner = this.querySelector('.bc-search-modal__spinner');
      this.body = this.querySelector('.bc-search-modal__body');
      this.status = this.querySelector('.bc-search-modal__status');

      const raw = this.dataset.popularTerms || '';
      this._popularTerms = raw.split(',').map(t => t.trim()).filter(Boolean);

      this._bindEvents();
      this._initVoiceSearch();
      this._renderPopularSearches();
    }

    /* ── Event binding ─────────────────────────────────────── */
    _bindEvents() {
      /* Input — debounced fetch */
      this.input.addEventListener('input', debounce((e) => {
        const q = e.target.value.trim();
        if (q.length === 0) {
          this._clearResults();
          this._renderPopularSearches();
          this._setStatus('');
          this._setLoading(false);
          return;
        }
        if (q === this._lastQuery) return;
        this._lastQuery = q;
        this._fetchResults(q);
      }, 280));

      /* Keyboard nav */
      this.input.addEventListener('keydown', (e) => this._handleKeyNav(e));

      /* Close button */
      this.closeBtn?.addEventListener('click', () => this.close());

      /* Backdrop click */
      this.backdrop?.addEventListener('click', (e) => {
        if (e.target === this.backdrop) this.close();
      });

      /* Escape key */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._isOpen) this.close();
      });

      /* ⌘K / Ctrl+K global shortcut */
      document.addEventListener('keydown', (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          if (this._isOpen) { this.close(); } else { this.open(); }
        }
      });
    }

    /* ── Open/Close ────────────────────────────────────────── */
    open() {
      if (this._isOpen) return;
      this._isOpen = true;

      this.backdrop.classList.add('is-active');
      document.body.style.overflow = 'hidden';

      if (this.input) this.input.setAttribute('aria-expanded', 'true');

      setTimeout(() => {
        this.input.focus();
        this.input.select();
      }, 60);

      this._trapCleanup = trapFocus(this.modal);
      this.dispatchEvent(new CustomEvent('bc:search:open', { bubbles: true }));
    }

    close() {
      if (!this._isOpen) return;
      this._isOpen = false;

      this.backdrop.classList.remove('is-active');
      document.body.style.overflow = '';

      if (this.input) this.input.setAttribute('aria-expanded', 'false');

      this._trapCleanup?.();
      this._trapCleanup = null;
      this._abortController?.abort();

      /* Stop voice recognition if active */
      if (this._recognition && this.voiceBtn?.classList.contains('is-listening')) {
        this._recognition.stop();
      }

      this.dispatchEvent(new CustomEvent('bc:search:close', { bubbles: true }));
    }

    /* ── Fetch from Shopify Predictive Search API ──────────── */
    _fetchResults(query) {
      this._abortController?.abort();
      this._abortController = new AbortController();
      this._setLoading(true);

      const params = new URLSearchParams({
        q: query,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,article,page,query',
        'resources[limit]': '8',
        'resources[options][fields]': 'title,product_type,vendor,tag,variants.title',
        'resources[options][unavailable_products]': 'last'
      });

      fetch(`/search/suggest?${params}`, {
        signal: this._abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(res => {
          if (!res.ok) throw new Error(`Search failed: ${res.status}`);
          return res.text();
        })
        .then(html => {
          this._setLoading(false);
          this._renderResults(html, query);
        })
        .catch(err => {
          if (err.name === 'AbortError') return;
          this._setLoading(false);
          this._renderError();
        });
    }

    /* ── Render results from section HTML ──────────────────── */
    _renderResults(html, query) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionEl = doc.getElementById('shopify-section-predictive-search');

      if (!sectionEl) { this._renderEmpty(query); return; }

      const inner = sectionEl.querySelector('#bc-predictive-results');
      if (!inner) { this._renderEmpty(query); return; }

      const itemCount = inner.querySelectorAll('.bc-ps__item').length;
      this._clearResults();

      if (itemCount === 0) { this._renderEmpty(query); return; }

      this.body.appendChild(inner.cloneNode(true));

      const viewAll = this._buildViewAll(query);
      this.body.appendChild(viewAll);

      const productCount = inner.querySelectorAll('.bc-ps__item--product').length;
      this._setStatus(
        productCount > 0
          ? `${productCount} product${productCount > 1 ? 's' : ''} found for "${query}"`
          : `Results found for "${query}"`
      );
    }

    /* ── Empty state ───────────────────────────────────────── */
    _renderEmpty(query) {
      const el = document.createElement('div');
      el.className = 'bc-ps__empty';
      el.innerHTML = `
        <svg class="bc-ps__empty-icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <circle cx="22" cy="22" r="14"/><path d="m36 36 6 6"/>
          <path d="M22 16v6M22 26h.01" stroke-linecap="round"/>
        </svg>
        <p class="bc-ps__empty-title">No results for &ldquo;${this._escape(query)}&rdquo;</p>
        <p class="bc-ps__empty-text">Try a different search term or browse our collections.</p>
      `;
      this.body.appendChild(el);
      this._setStatus(`No results for "${query}"`);
    }

    /* ── Error state ───────────────────────────────────────── */
    _renderError() {
      const el = document.createElement('div');
      el.className = 'bc-ps__empty';
      el.innerHTML = `
        <p class="bc-ps__empty-title">Something went wrong</p>
        <p class="bc-ps__empty-text">Please try again in a moment.</p>
      `;
      this.body.appendChild(el);
    }

    /* ── Popular searches ──────────────────────────────────── */
    _renderPopularSearches() {
      if (!this._popularTerms.length) return;

      const container = document.createElement('div');
      container.className = 'bc-ps__popular';
      container.setAttribute('aria-label', 'Popular searches');

      const label = document.createElement('p');
      label.className = 'bc-ps__popular-label';
      label.textContent = 'Popular searches';
      container.appendChild(label);

      const ul = document.createElement('ul');
      ul.className = 'bc-ps__popular-list';

      this._popularTerms.forEach(term => {
        const li = document.createElement('li');
        li.className = 'bc-ps__popular-item';
        const a = document.createElement('a');
        a.href = `/search?q=${encodeURIComponent(term)}&options[prefix]=last`;
        a.addEventListener('click', (e) => {
          e.preventDefault();
          this.input.value = term;
          this.clearBtn.classList.add('is-visible');
          this._lastQuery = term;
          this._fetchResults(term);
        });
        a.innerHTML = `
          <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <circle cx="9" cy="9" r="6"/><path d="m17 17-3.5-3.5"/>
          </svg>
          ${this._escape(term)}
        `;
        li.appendChild(a);
        ul.appendChild(li);
      });

      container.appendChild(ul);
      this.body.appendChild(container);
    }

    /* ── View all results link ─────────────────────────────── */
    _buildViewAll(query) {
      const link = document.createElement('a');
      link.className = 'bc-ps__view-all';
      link.href = `/search?q=${encodeURIComponent(query)}&options[prefix]=last&type=product`;
      link.innerHTML = `
        View all results for &ldquo;<strong>${this._escape(query)}</strong>&rdquo;
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M4 10h12M10 4l6 6-6 6"/>
        </svg>
      `;
      return link;
    }

    /* ── Voice Search (Web Speech API) ─────────────────────── */
    _initVoiceSearch() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition || !this.voiceBtn) return;

      /* Show button since browser supports it */
      this.voiceBtn.classList.add('is-supported');

      this._recognition = new SpeechRecognition();
      this._recognition.continuous = false;
      this._recognition.lang = document.documentElement.lang || 'en-US';
      this._recognition.interimResults = false;
      this._recognition.maxAlternatives = 1;

      this._recognition.onstart = () => {
        this.voiceBtn.classList.add('is-listening');
        this.input.placeholder = 'Listening...';
        this.input.value = '';
      };

      this._recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        this.input.value = transcript;
        this._lastQuery = transcript;
        this._fetchResults(transcript);
      };

      this._recognition.onspeechend = () => {
        this._recognition.stop();
      };

      this._recognition.onend = () => {
        this.voiceBtn.classList.remove('is-listening');
        this.input.placeholder = this.input.getAttribute('aria-label') || 'Search products, articles…';
      };

      this._recognition.onerror = (event) => {
        this.voiceBtn.classList.remove('is-listening');
        this.input.placeholder = this.input.getAttribute('aria-label') || 'Search products, articles…';
        if (event.error !== 'aborted' && event.error !== 'no-speech') {
          this._setStatus(`Voice search error: ${event.error}`);
        }
      };

      this.voiceBtn.addEventListener('click', () => {
        if (this.voiceBtn.classList.contains('is-listening')) {
          this._recognition.stop();
        } else {
          this._recognition.start();
        }
      });
    }

    /* ── Keyboard navigation ───────────────────────────────── */
    _handleKeyNav(e) {
      const items = [...this.body.querySelectorAll('a[href], button')];
      if (!items.length) return;

      const current = document.activeElement;
      const idx = items.indexOf(current);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = idx < items.length - 1 ? items[idx + 1] : items[0];
        next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (idx <= 0) { this.input.focus(); return; }
        items[idx - 1].focus();
      }
    }

    /* ── Helpers ───────────────────────────────────────────── */
    _clearResults() {
      this.body.innerHTML = '';
    }

    _setLoading(state) {
      this.spinner?.classList.toggle('is-active', state);
    }

    _setStatus(msg) {
      if (this.status) this.status.textContent = msg;
    }

    _escape(str) {
      return str.replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }
  }

  /* Register custom element */
  customElements.define('predictive-search-modal', PredictiveSearchModal);

  /* ════════════════════════════════════════════════════════════
     Wire up trigger buttons
     Any [data-search-trigger] element opens the modal.
     ════════════════════════════════════════════════════════════ */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-search-trigger]');
    if (!trigger) return;
    e.preventDefault();

    const targetId = trigger.dataset.searchTarget || 'PredictiveSearchModal';
    const modal = document.getElementById(targetId);
    if (modal instanceof PredictiveSearchModal) modal.open();
  });

  /* ── "/" shortcut to open search ─────────────────────────── */
  document.addEventListener('keydown', (e) => {
    if (e.key === '/' && document.activeElement.tagName !== 'INPUT' &&
        document.activeElement.tagName !== 'TEXTAREA') {
      const modal = document.getElementById('PredictiveSearchModal');
      if (modal instanceof PredictiveSearchModal && !modal._isOpen) {
        e.preventDefault();
        modal.open();
      }
    }
  });

})();
